// loadsSummary.tsx
// Dashboard panel: completed-vs-outstanding loads for today/tomorrow and prior-day
// stats, grouped by producer or carrier with drilldown. Reuses SQLTable + apiService.
import React, { useEffect, useMemo, useState } from 'react';
import SQLTable from '../../../components/sql_table/tableMain';
import { ChevronDown, ChevronRight, CheckCircle2, Clock, Truck, Layers, Info, Droplet, Percent, AlertTriangle, X } from 'lucide-react';
import { useApiData } from '../../../contexts/apiService';
import { useDataManager } from '../../../contexts/dataManager';
import { useUserAccess, USER_ROLE_LEVEL } from '../../../contexts/userAccess';
import {
    GroupBy,
    DayLoadRow,
    DayTotals,
    PriorDayRow,
    PriorDayTotals,
    getDayContext,
    aggregateDayLoads,
    summarizeDay,
    aggregatePriorDay,
    summarizePriorDay,
    filterDayLoads,
    filterUncompletedLoads,
    filterCompletedLoads,
    filterOutstandingLoads,
    filterWasteLoads,
    filterCanceledLoads,
    filterRejectedLoads,
    filterPriorCompletedLoads,
} from './loadsHelpers';
import {
    baseTableProps,
    detailCols,
    getDaySummaryCols,
    getPriorSummaryCols,
    statusRowColor,
    priorStatusRowColor,
} from './loadsSummaryTableConfig';
import './loadsSummary.css';

const MIN_LEVEL = 2; // scheduler and above

interface ToggleProps<T extends string> {
    options: { value: T; label: string; title?: string }[];
    value: T;
    onChange: (v: T) => void;
}
function Toggle<T extends string>({ options, value, onChange }: ToggleProps<T>) {
    return (
        <div className="loads-toggle">
            {options.map(opt => (
                <button
                    key={opt.value}
                    type="button"
                    className={`loads-toggle-btn loads-tip-anchor${value === opt.value ? ' active' : ''}`}
                    onClick={() => onChange(opt.value)}
                >
                    {opt.label}
                    {opt.title && <span className="loads-tip loads-tip--top">{opt.title}</span>}
                </button>
            ))}
        </div>
    );
}

const PanelTitle: React.FC<{ open: boolean; onToggle: () => void; tooltip?: string; children: React.ReactNode }> = ({
    open,
    onToggle,
    tooltip,
    children,
}) => (
    <button
        type="button"
        className={`loads-panel-toggle${tooltip ? ' loads-tip-anchor' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
    >
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <h3 className="loads-panel-title">{children}</h3>
        {tooltip && <span className="loads-tip loads-tip--bottom" role="tooltip">{tooltip}</span>}
    </button>
);

// KPI stat card shown in the row at the top of each panel. An optional `tooltip`
// adds a small info icon next to the label explaining what the metric counts.
type StatVariant = 'total' | 'completed' | 'outstanding' | 'waste' | 'volume' | 'bsw' | 'incomplete';
interface StatCardProps {
    icon: React.ReactNode;
    value: React.ReactNode;
    label: string;
    sub?: React.ReactNode;
    variant: StatVariant;
    tooltip?: string;
    onClick?: () => void;
    active?: boolean;
}
const StatCard: React.FC<StatCardProps> = ({ icon, value, label, sub, variant, tooltip, onClick, active }) => {
    const clickable = !!onClick;
    return (
        <div
            className={`loads-stat-card loads-stat-${variant}${active ? ' active' : ''}${clickable ? ' clickable' : ''}`}
            onClick={onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-pressed={clickable ? !!active : undefined}
            onKeyDown={
                clickable
                    ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick!(); } }
                    : undefined
            }
        >
            <span className="loads-stat-value">{value}</span>
            <span className="loads-stat-body">
                <span className="loads-stat-label">
                    {label}
                    {tooltip && (
                        <span className="loads-stat-info loads-tip-anchor" tabIndex={0} aria-label={tooltip}>
                            <Info size={13} />
                            <span className="loads-tip loads-tip--bottom" role="tooltip">{tooltip}</span>
                        </span>
                    )}
                </span>
                {sub != null && <span className="loads-stat-sub">{sub}</span>}
            </span>
            <span className="loads-stat-icon">{icon}</span>
            {clickable && <span className="loads-stat-hint">Click for details</span>}
        </div>
    );
};

// Completion donut (CSS conic-gradient) with a center percentage and a legend.
interface DonutLegendItem {
    cls: string;
    label: string;
    value: number;
    onClick?: () => void; // when set, the legend row becomes clickable (opens a drilldown)
    active?: boolean;
}
interface DonutProps {
    pct: number;
    centerLabel: string;
    legend: DonutLegendItem[];
}
// Ring segment colors keyed by legend `cls`. Values are CSS custom properties defined in
// loadsSummary.css (--loads-*) — the single source for the status palette; resolved via
// var() in the inline conic-gradient below.
const DONUT_COLORS: Record<string, string> = {
    completed: 'var(--loads-completed)',
    outstanding: 'var(--loads-outstanding)',
    incomplete: 'var(--loads-outstanding)',
    canceled: 'var(--loads-canceled)',
    rejected: 'var(--loads-rejected)',
};
const Donut: React.FC<DonutProps> = ({ pct, centerLabel, legend }) => {
    // Build the ring straight from the legend so every bucket (completed / outstanding
    // / canceled / rejected) shows as its own colored segment. The legend must partition
    // the whole (its values sum to the total shown).
    const total = legend.reduce((sum, l) => sum + l.value, 0);
    let acc = 0;
    const stops = legend
        .filter(l => l.value > 0)
        .map(l => {
            const start = (acc / total) * 100;
            acc += l.value;
            const end = (acc / total) * 100;
            return `${DONUT_COLORS[l.cls] ?? 'var(--loads-donut-empty)'} ${start}% ${end}%`;
        });
    const ring = total > 0 ? `conic-gradient(${stops.join(', ')})` : 'conic-gradient(var(--loads-donut-empty) 0 100%)';
    return (
    <div className="loads-donut-wrap">
        <div
            className="loads-donut"
            style={{ background: ring }}
            role="img"
            aria-label={`${Math.round(pct)}% ${centerLabel}`}
        >
            <div className="loads-donut-hole">
                <span className="loads-donut-pct">{Math.round(pct)}%</span>
                <span className="loads-donut-sub">{centerLabel}</span>
            </div>
        </div>
        <div className="loads-donut-legend">
            {legend.map(l => {
                const clickable = !!l.onClick;
                return (
                    <span
                        key={l.label}
                        className={clickable ? `loads-legend-clickable${l.active ? ' active' : ''}` : undefined}
                        onClick={l.onClick}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-pressed={clickable ? !!l.active : undefined}
                        onKeyDown={
                            clickable
                                ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); l.onClick!(); } }
                                : undefined
                        }
                    >
                        <i className={`dot ${l.cls}`} />{l.label}<b>{l.value}</b>
                    </span>
                );
            })}
        </div>
    </div>
    );
};

// Drilldown detail table shown under a summary row / KPI card when one is selected.
// `rowColor` defaults to the notes-based coloring (same-day panels); prior-day
// drilldowns pass priorStatusRowColor so colors match their status-based counts.
const Drilldown: React.FC<{ title: React.ReactNode; tableName: string; data: any[]; onClose: () => void; rowColor?: any }> = ({
    title,
    tableName,
    data,
    onClose,
    rowColor = statusRowColor,
}) => (
    <div className="loads-drilldown">
        <div className="loads-drilldown-header">
            <span>{title}</span>
            <button type="button" className="loads-close-btn" onClick={onClose} aria-label="Close">
                <X size={18} />
            </button>
        </div>
        <SQLTable
            {...baseTableProps}
            tableName={tableName}
            idColumn="id"
            tableData={data}
            columnConfigs={detailCols}
            rowColor={rowColor}
        />
    </div>
);

const LoadsSummary: React.FC = () => {
    const { apiData } = useApiData();
    const { requestTableData, hasTableData, isTableLoading } = useDataManager();
    const { userRole = 'viewer' } = useUserAccess();
    const canView = USER_ROLE_LEVEL[userRole] >= MIN_LEVEL;

    const dayCtx = useMemo(() => getDayContext(), []);

    const [daySel, setDaySel] = useState<'today' | 'tomorrow'>('today');
    const [groupByA, setGroupByA] = useState<GroupBy>('carrier');
    const [groupByB, setGroupByB] = useState<GroupBy>('carrier');
    const [selectedGroupA, setSelectedGroupA] = useState<string | null>(null);
    const [selectedGroupB, setSelectedGroupB] = useState<string | null>(null);
    const [cardDrillA, setCardDrillA] = useState<'completed' | 'outstanding' | 'waste' | 'canceled' | 'rejected' | null>(null);
    const [cardDrillB, setCardDrillB] = useState<'completed' | 'uncompleted' | 'canceled' | 'rejected' | null>(null);
    const [openA, setOpenA] = useState(false);
    const [openB, setOpenB] = useState(false);

    // Fetch the crude schedule for every month partition the relevant days fall in
    // (normally one; up to three at month boundaries). The hasTableData guard de-dupes.
    useEffect(() => {
        if (!canView) return;
        dayCtx.months.forEach(month => {
            if (!hasTableData('schedule_table', month)) requestTableData('schedule_table', month);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canView, dayCtx]);

    // Reset drilldowns when the user changes a toggle.
    useEffect(() => { setSelectedGroupA(null); setCardDrillA(null); }, [daySel, groupByA]);
    useEffect(() => { setSelectedGroupB(null); setCardDrillB(null); }, [groupByB]);

    const crude = (apiData?.schedule_table ?? []) as any[];
    const selectedYmd = daySel === 'today' ? dayCtx.today : dayCtx.tomorrow;

    const dayRows = useMemo<DayLoadRow[]>(
        () => aggregateDayLoads(crude, selectedYmd, groupByA),
        [crude, selectedYmd, groupByA]
    );
    // Ungrouped totals for the KPI cards.
    const dayTotals = useMemo<DayTotals>(
        () => summarizeDay(crude, selectedYmd),
        [crude, selectedYmd]
    );
    // Completion rate = completed out of the loads meant to be delivered — i.e. everything
    // EXCEPT Canceled (which were pulled from the schedule). Rejected and outstanding count
    // against it, so a day with rejects can't read 100%.
    const dayBase = dayTotals.totalLoads - dayTotals.canceledLoads;
    const completionPct = dayBase > 0 ? (dayTotals.completed / dayBase) * 100 : 0;
    const priorRows = useMemo<PriorDayRow[]>(
        () => aggregatePriorDay(crude, dayCtx.yesterday, groupByB),
        [crude, dayCtx.yesterday, groupByB]
    );
    // Ungrouped prior-day totals for the KPI cards + donut (complete vs incomplete).
    const priorTotals = useMemo<PriorDayTotals>(
        () => summarizePriorDay(crude, dayCtx.yesterday),
        [crude, dayCtx.yesterday]
    );
    // Completed prior-day loads (green donut slice) — active loads completed by status or
    // notes. summarizePriorDay already tallies these, so reuse it rather than re-deriving.
    const priorDone = priorTotals.completedLoads;
    // Completion rate = done out of everything meant to be delivered (all but Canceled);
    // Rejected and uncompleted count against it, so rejects can't read 100%.
    const priorBase = priorTotals.totalLoads - priorTotals.canceledLoads;
    const priorCompletePct = priorBase > 0 ? (priorDone / priorBase) * 100 : 0;
    const detailA = useMemo(
        () => (selectedGroupA ? filterDayLoads(crude, selectedYmd, groupByA, selectedGroupA) : []),
        [crude, selectedYmd, groupByA, selectedGroupA]
    );
    const cardDrillData = useMemo(() => {
        switch (cardDrillA) {
            case 'completed': return filterCompletedLoads(crude, selectedYmd);
            case 'outstanding': return filterOutstandingLoads(crude, selectedYmd);
            case 'waste': return filterWasteLoads(crude, selectedYmd);
            case 'canceled': return filterCanceledLoads(crude, selectedYmd);
            case 'rejected': return filterRejectedLoads(crude, selectedYmd);
            default: return [];
        }
    }, [cardDrillA, crude, selectedYmd]);
    const detailB = useMemo(
        () => (selectedGroupB ? filterDayLoads(crude, dayCtx.yesterday, groupByB, selectedGroupB) : []),
        [crude, dayCtx.yesterday, groupByB, selectedGroupB]
    );
    const cardDrillBData = useMemo(() => {
        const ymd = dayCtx.yesterday;
        switch (cardDrillB) {
            case 'completed': return filterPriorCompletedLoads(crude, ymd);
            case 'uncompleted': return filterUncompletedLoads(crude, ymd);
            case 'canceled': return filterCanceledLoads(crude, ymd);
            case 'rejected': return filterRejectedLoads(crude, ymd);
            default: return [];
        }
    }, [cardDrillB, crude, dayCtx.yesterday]);

    const isLoading = isTableLoading('schedule_table');

    if (!canView) return null;

    // Props that make a Panel A KPI card / donut-legend row open its matching drilldown
    // (only when >0). Shared by the cards and the clickable donut legend.
    const dayCardProps = (key: 'completed' | 'outstanding' | 'waste' | 'canceled' | 'rejected', count: number) => ({
        active: cardDrillA === key,
        onClick: count > 0
            ? () => { setSelectedGroupA(null); setCardDrillA(c => (c === key ? null : key)); }
            : undefined,
    });

    // Panel B equivalent — shared by the Uncompleted card and the clickable prior-day legend.
    const priorCardProps = (key: 'completed' | 'uncompleted' | 'canceled' | 'rejected', count: number) => ({
        active: cardDrillB === key,
        onClick: count > 0
            ? () => { setSelectedGroupB(null); setCardDrillB(c => (c === key ? null : key)); }
            : undefined,
    });

    const daySummaryCols = getDaySummaryCols(groupByA);
    const priorSummaryCols = getPriorSummaryCols(groupByB);

    const groupToggleOptions: { value: GroupBy; label: string }[] = [
        { value: 'producer', label: 'Producer' },
        { value: 'carrier', label: 'Carrier' },
    ];

    return (
        <div className="loads-summary">
            <h2 className="loads-summary-title">Loads Summary</h2>

            {/* Panel A — Today / Tomorrow: completed vs outstanding */}
            <section className="loads-panel">
                <div className="loads-panel-header">
                    <PanelTitle open={openA} onToggle={() => setOpenA(o => !o)}>
                        Completed vs Outstanding Loads
                    </PanelTitle>
                    {openA && (
                        <div className="loads-controls">
                            <Toggle
                                options={[
                                    { value: 'today', label: 'Today', title: dayCtx.today },
                                    { value: 'tomorrow', label: 'Tomorrow', title: dayCtx.tomorrow },
                                ]}
                                value={daySel}
                                onChange={setDaySel}
                            />
                            <Toggle options={groupToggleOptions} value={groupByA} onChange={setGroupByA} />
                        </div>
                    )}
                </div>
                {openA && (
                    <>
                        <div className="loads-kpi">
                            <div className="loads-stat-grid">
                                <StatCard
                                    variant="total"
                                    icon={<Layers size={20} />}
                                    value={dayTotals.totalLoads}
                                    label="Total Loads"
                                />
                                <StatCard
                                    variant="completed"
                                    icon={<CheckCircle2 size={20} />}
                                    value={dayTotals.completed}
                                    label="Completed"
                                    {...dayCardProps('completed', dayTotals.completed)}
                                />
                                <StatCard
                                    variant="outstanding"
                                    icon={<Clock size={20} />}
                                    value={dayTotals.outstanding}
                                    label="Outstanding"
                                    sub={
                                        dayTotals.unassignedCarrier > 0
                                            ? `${dayTotals.unassignedCarrier} unassigned carrier`
                                            : undefined
                                    }
                                    {...dayCardProps('outstanding', dayTotals.outstanding)}
                                />
                                <StatCard
                                    variant="waste"
                                    icon={<Truck size={20} />}
                                    value={dayTotals.wasteLoads}
                                    label="Waste Loads"
                                    tooltip="Crude loads routed to a waste destination (Orla processing or Sandpoint)."
                                    {...dayCardProps('waste', dayTotals.wasteLoads)}
                                />
                            </div>
                        </div>
                        {isLoading && dayRows.length === 0 ? (
                            <p className="loads-empty">Loading schedule…</p>
                        ) : dayRows.length === 0 ? (
                            <p className="loads-empty">No loads scheduled for this day.</p>
                        ) : (
                            <div className="loads-day-body">
                                <div className="loads-day-table">
                                    <SQLTable
                                        {...baseTableProps}
                                        tableName="loads_day_summary"
                                        idColumn="group"
                                        tableData={dayRows}
                                        columnConfigs={daySummaryCols}
                                        onRowClick={(row) => { setSelectedGroupA(row.group); setCardDrillA(null); }}
                                    />
                                </div>
                                {dayTotals.totalLoads > 0 && (
                                    <Donut
                                        pct={completionPct}
                                        centerLabel="completed"
                                        legend={[
                                            { cls: 'completed', label: 'Completed', value: dayTotals.completed, ...dayCardProps('completed', dayTotals.completed) },
                                            { cls: 'outstanding', label: 'Outstanding', value: dayTotals.outstanding, ...dayCardProps('outstanding', dayTotals.outstanding) },
                                            { cls: 'canceled', label: 'Canceled', value: dayTotals.canceledLoads, ...dayCardProps('canceled', dayTotals.canceledLoads) },
                                            { cls: 'rejected', label: 'Rejected', value: dayTotals.rejectedLoads, ...dayCardProps('rejected', dayTotals.rejectedLoads) },
                                        ]}
                                    />
                                )}
                            </div>
                        )}
                        {selectedGroupA && (
                            <Drilldown
                                title={<>Loads for <strong>{selectedGroupA}</strong></>}
                                tableName="loads_day_detail"
                                data={detailA}
                                onClose={() => setSelectedGroupA(null)}
                            />
                        )}
                        {cardDrillA && (
                            <Drilldown
                                title={<strong>{cardDrillA[0].toUpperCase() + cardDrillA.slice(1)} loads</strong>}
                                tableName={`loads_day_${cardDrillA}`}
                                data={cardDrillData}
                                onClose={() => setCardDrillA(null)}
                            />
                        )}
                    </>
                )}
            </section>

            {/* Panel B — Prior day stats */}
            <section className="loads-panel">
                <div className="loads-panel-header">
                    <PanelTitle open={openB} onToggle={() => setOpenB(o => !o)} tooltip={dayCtx.yesterday}>
                        Prior Day Statistics
                    </PanelTitle>
                    {openB && (
                        <div className="loads-controls">
                            <Toggle options={groupToggleOptions} value={groupByB} onChange={setGroupByB} />
                        </div>
                    )}
                </div>
                {openB && (
                    <>
                        <div className="loads-kpi">
                            <div className="loads-stat-grid">
                                <StatCard
                                    variant="total"
                                    icon={<Layers size={20} />}
                                    value={priorTotals.totalLoads}
                                    label="Total Loads"
                                />
                                <StatCard
                                    variant="volume"
                                    icon={<Droplet size={20} />}
                                    value={Math.round(priorTotals.totalVolume).toLocaleString()}
                                    label="Volume (bbl)"
                                />
                                <StatCard
                                    variant="bsw"
                                    icon={<Percent size={20} />}
                                    value={priorTotals.avgBsw != null ? priorTotals.avgBsw.toFixed(2) : '—'}
                                    label="Avg BSW"
                                />
                                <StatCard
                                    variant="incomplete"
                                    icon={<AlertTriangle size={20} />}
                                    value={priorTotals.incompleteLoads}
                                    label="Uncompleted"
                                    tooltip="An active load is uncompleted if it isn't marked completed — by status or by notes (a “Completed” note counts, “on track” doesn't). Canceled/Rejected are shown separately under the donut. Click to see them."
                                    {...priorCardProps('uncompleted', priorTotals.incompleteLoads)}
                                />
                            </div>
                        </div>
                        {isLoading && priorRows.length === 0 ? (
                            <p className="loads-empty">Loading schedule…</p>
                        ) : priorRows.length === 0 ? (
                            <p className="loads-empty">No loads recorded for the prior day.</p>
                        ) : (
                            <div className="loads-day-body">
                                <div className="loads-day-table">
                                    <SQLTable
                                        {...baseTableProps}
                                        tableName="loads_prior_summary"
                                        idColumn="group"
                                        tableData={priorRows}
                                        columnConfigs={priorSummaryCols}
                                        onRowClick={(row) => { setSelectedGroupB(row.group); setCardDrillB(null); }}
                                    />
                                </div>
                                {priorTotals.totalLoads > 0 && (
                                    <Donut
                                        pct={priorCompletePct}
                                        centerLabel="completed"
                                        legend={[
                                            { cls: 'completed', label: 'Completed', value: priorDone, ...priorCardProps('completed', priorDone) },
                                            { cls: 'incomplete', label: 'Uncompleted', value: priorTotals.incompleteLoads, ...priorCardProps('uncompleted', priorTotals.incompleteLoads) },
                                            { cls: 'canceled', label: 'Canceled', value: priorTotals.canceledLoads, ...priorCardProps('canceled', priorTotals.canceledLoads) },
                                            { cls: 'rejected', label: 'Rejected', value: priorTotals.rejectedLoads, ...priorCardProps('rejected', priorTotals.rejectedLoads) },
                                        ]}
                                    />
                                )}
                            </div>
                        )}
                        {selectedGroupB && (
                            <Drilldown
                                title={<>Loads for <strong>{selectedGroupB}</strong></>}
                                tableName="loads_prior_detail"
                                data={detailB}
                                onClose={() => setSelectedGroupB(null)}
                                rowColor={priorStatusRowColor}
                            />
                        )}
                        {cardDrillB && (
                            <Drilldown
                                title={<strong>{cardDrillB[0].toUpperCase() + cardDrillB.slice(1)} loads</strong>}
                                tableName={`loads_prior_${cardDrillB}`}
                                data={cardDrillBData}
                                onClose={() => setCardDrillB(null)}
                                rowColor={priorStatusRowColor}
                            />
                        )}
                    </>
                )}
            </section>
        </div>
    );
};

export default LoadsSummary;
